import { useRef, type CSSProperties, type ReactElement } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { GitBranch, Layers, Users, Zap } from "lucide-react";
import { color, radius, space, typography, motion as motionTokens } from "@tokens/index";
import { SlidingPill, useViewport } from "@primitives/index";
import { ShellFrame } from "./workflow-layout/ShellFrame";
import { TopBar } from "./workflow-layout/TopBar";
import { BackToWorkflows, ChromeActions } from "./workflow-layout/ChromeActions";
import { contentGutter } from "./workflow-layout/shellMeasures";

// ---------------------------------------------------------------------------
// Permission seam
//
// Real role/permission enforcement lands in a follow-up. The descriptor list
// records which area needs which capability so the filter step inside
// `AdminBar` becomes the single integration point when permissions arrive.
// ---------------------------------------------------------------------------

type AdminPermission =
    | "queues.read"
    | "managementGroups.read"
    | "routingRules.read";

interface AdminNavEntry {
    readonly id: string;
    readonly label: string;
    readonly icon: typeof Layers;
    readonly to: string;
    readonly requiredPermission?: AdminPermission;
}

const ADMIN_NAV_ENTRIES: readonly AdminNavEntry[] = [
    { id: "queues", label: "Queues", icon: Layers, to: "/queues" },
    {
        id: "management-groups",
        label: "Management groups",
        icon: Users,
        to: "/management-groups",
    },
    {
        id: "routing-rules",
        label: "Routing rules",
        icon: GitBranch,
        to: "/routing-rules",
    },
    {
        id: "next-ranking",
        label: "Next ranking",
        icon: Zap,
        to: "/next-ranking",
    },
];

// ---------------------------------------------------------------------------
// AdminBar — descriptor-driven horizontal nav under the top bar.
//
// A labeled pill row on `expanded`+. On `compact` and `medium` it becomes a
// `[data-scroll-row]`: horizontal scroll, snap, hidden scrollbar, edge fade
// (the global stylesheet owns those rules). The selected pill is drawn by
// `SlidingPill`.
// ---------------------------------------------------------------------------

const adminBarBaseStyle: CSSProperties = {
    position: "relative",
    zIndex: 15,
    display: "flex",
    alignItems: "center",
    borderBottom: `1px solid ${color.border.subtle}`,
    background: color.bg.surface,
    flexShrink: 0,
};

// The pill track is a child of the scroll row, not the scroll row itself, so
// `SlidingPill` measures in a coordinate space that scrolls with the pills.
const pillTrackStyle: CSSProperties = {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: space[1],
    paddingBlock: space[2],
    minWidth: 0,
};

const adminNavLinkStyle = (isActive: boolean): CSSProperties => ({
    position: "relative",
    zIndex: 1,
    display: "inline-flex",
    alignItems: "center",
    gap: space[2],
    minHeight: 36,
    paddingInline: space[3],
    borderRadius: radius.pill,
    textDecoration: "none",
    color: isActive ? color.accent.primary : color.fg.muted,
    fontFamily: typography.family.system,
    fontSize: typography.scale.sm,
    fontWeight: isActive ? typography.weights.semibold : typography.weights.medium,
    lineHeight: 1,
    whiteSpace: "nowrap",
    transition: `color ${motionTokens.durations.fast} ${motionTokens.easings.standard}`,
    cursor: "pointer",
});

function isEntryActive(pathname: string, entry: AdminNavEntry): boolean {
    return pathname === entry.to || pathname.startsWith(`${entry.to}/`);
}

function AdminBar(): ReactElement {
    // When permissions arrive, `.filter((e) => permissions.has(e.requiredPermission ?? "*"))`
    // is the single integration point. Until then every entry is visible.
    const entries = ADMIN_NAV_ENTRIES;
    const { viewport, isAtLeast } = useViewport();
    const { pathname } = useLocation();
    const trackRef = useRef<HTMLDivElement>(null);
    const scrolls = !isAtLeast("expanded");
    const activeIndex = entries.findIndex((entry) => isEntryActive(pathname, entry));
    // The edge fade is 24px wide; the track's padding keeps the first pill
    // clear of it.
    const trackPadding = scrolls ? space[6] : contentGutter(viewport);

    return (
        <nav
            aria-label="Admin sections"
            style={adminBarBaseStyle}
            {...(scrolls ? { "data-scroll-row": "" } : {})}
        >
            <div ref={trackRef} style={{ ...pillTrackStyle, paddingInline: trackPadding }}>
                <SlidingPill
                    containerRef={trackRef}
                    activeIndex={activeIndex}
                    itemSelector="[data-admin-nav-item]"
                    style={{ borderRadius: radius.pill, background: color.bg.selected }}
                />
                {entries.map((entry) => {
                    const Icon = entry.icon;
                    return (
                        <NavLink
                            key={entry.id}
                            to={entry.to}
                            data-admin-nav-item=""
                            data-hit-area=""
                            style={({ isActive }) => ({
                                ...adminNavLinkStyle(isActive),
                                scrollSnapAlign: "start",
                            })}
                        >
                            <Icon size={14} strokeWidth={2.25} aria-hidden="true" />
                            {entry.label}
                        </NavLink>
                    );
                })}
            </div>
        </nav>
    );
}

// ---------------------------------------------------------------------------
// AdminLayout — layout route for the admin area.
//
// Wraps every admin sub-route with the shared chrome and the AdminBar. The
// wrapped pages render through <Outlet/> and own their own content width.
// ---------------------------------------------------------------------------

const contentStyle: CSSProperties = {
    width: "100%",
    minWidth: 0,
};

export function AdminLayout(): ReactElement {
    return (
        <ShellFrame
            topBar={<TopBar leading={<BackToWorkflows />} actions={<ChromeActions />} />}
            subBar={<AdminBar />}
            content="scroll"
        >
            <div style={contentStyle}>
                <Outlet />
            </div>
        </ShellFrame>
    );
}
